import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Utensils, BookOpen, Moon, Shirt, Wallet } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PHASES, BODY_TYPES, FAITHS, ZODIAC } from "@/lib/lunelle-data";
import { styleOfDay, styleForPhase, phaseStyle } from "@/lib/zodiac-style";
import { useBudget, computeBudget, formatMoney } from "@/lib/budget";
import { goTo } from "@/lib/api";
import { useTodayPhase } from "@/lib/cycle";
import { Reveal, MaskLines, Eyebrow } from "./Reveal";
import { useProfile } from "@/lib/account";
import { useFaith } from "@/lib/useFaith";
import { mealFor, whyFor, DIET } from "@/lib/faith-customs";

const Field = ({ label, value, onChange, options, testId }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#d9c7bd]">{label}</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="mt-2 h-14 rounded-2xl border-white/15 bg-white/5 px-5 font-serif text-xl text-[#FBF5EE] focus:ring-[#E8B99A]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="rounded-2xl border-[#C86D51]/20 bg-[#FDFBF7]">
        {options.map((o) => <SelectItem key={o.id} value={o.id} data-testid={`${testId}-option-${o.id}`}>{o.name}</SelectItem>)}
      </SelectContent>
    </Select>
  </label>
);

const Row = ({ icon: Icon, title, children }) => (
  <div className="border-t border-[#C86D51]/15 py-5">
    <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9E4429]"><Icon className="h-3.5 w-3.5" />{title}</span>
    <div className="mt-2 text-[#3D302B]">{children}</div>
  </div>
);

export const Demo = () => {
  const [phase, setPhase] = useState("luteal");
  const [body, setBody] = useState("meso");
  const [faith, setFaith] = useState("christian");
  const [zodiac, setZodiac] = useState("leo");
  const today = useTodayPhase();
  const { member, zodiac: mySign, showZodiac, profile } = useProfile();
  const [myFaith] = useFaith();
  useEffect(() => {
    if (!member) return;
    setFaith(myFaith);
    if (mySign) setZodiac(mySign);
    if (profile?.body_type) setBody(profile.body_type);
  }, [member, myFaith, mySign, profile?.body_type]);
  const todayPhase = today?.phase;
  useEffect(() => {
    if (todayPhase) setPhase(todayPhase);
  }, [todayPhase]);
  const synced = today && today.phase === phase;
  const p = PHASES.find((x) => x.id === phase);
  const f = FAITHS.find((x) => x.id === faith);
  const z = ZODIAC.find((x) => x.id === zodiac);
  const style = showZodiac ? styleOfDay(zodiac) : styleForPhase(phase);
  const look = phaseStyle(phase, style.colorOfDay[0]);
  const { budget } = useBudget();
  const spend = computeBudget(budget).phases.find((x) => x.id === phase);
  const line = f.lines[PHASES.indexOf(p) % 2];
  const key = `${phase}-${body}-${faith}-${zodiac}`;

  return (
    <section id="demo" className="relative bg-[#2C221E] py-28 text-[#FBF5EE] sm:py-40" data-testid="demo-section">
      <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-[1fr_1fr] lg:px-12">
        <div>
          <Reveal><Eyebrow light>06 — Try a day in Lunelle</Eyebrow></Reveal>
          <h2 className="mt-6 font-serif text-5xl font-light leading-[1] tracking-tight sm:text-6xl lg:text-7xl">
            <MaskLines inView delay={0} lines={["Tell us a little.", <em key="e" className="italic text-[#E8B99A]">We'll hold the rest.</em>]} />
          </h2>
          <Reveal delay={0.1} className="mt-12 grid gap-6 sm:grid-cols-2">
            <Field label="Cycle phase" value={phase} onChange={setPhase} options={PHASES} testId="interactive-demo-phase-select" />
            <Field label="Body type" value={body} onChange={setBody} options={BODY_TYPES} testId="interactive-demo-bodytype-select" />
            <Field label="Faith" value={faith} onChange={setFaith} options={FAITHS} testId="interactive-demo-religion-select" />
            {showZodiac && <Field label="Zodiac" value={zodiac} onChange={setZodiac} options={ZODIAC} testId="interactive-demo-zodiac-select" />}
          </Reveal>
        </div>

        <Reveal delay={0.15} className="relative">
          <div className="absolute -inset-8 rounded-full bg-[#9E4429]/25 blur-3xl" />
          <div className="relative mx-auto max-w-md rounded-[2.5rem] border border-white/10 bg-[#FDFBF7] p-7 text-[#2C221E] shadow-2xl sm:p-9" data-testid="interactive-demo-result-card">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A665C]" data-testid="demo-result-day">Today · Day {synced ? today.day : p.days[0] + 1}{synced ? " · from your calendar" : ""}</span>
              <span className="h-3 w-3 rounded-full" style={{ background: p.color }} />
            </div>
            <motion.div key={key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}>
              <p className="mt-4 font-serif text-4xl leading-tight" data-testid="demo-result-phase">{p.season}</p>
              <p className="mb-6 font-serif text-lg italic" style={{ color: p.color }}>{p.mood}</p>
              <Row icon={Utensils} title={`Your plate · ${BODY_TYPES.find((b) => b.id === body).name}${member ? ` · ${DIET[faith].label}` : ""}`}>
                <p data-testid="demo-result-meal">{mealFor(phase, body, member ? faith : null)}</p>
                <p className="mt-1.5 text-sm text-[#6B5A52]" data-testid="demo-result-meal-why">{whyFor(phase, body)}</p>
              </Row>
              <Row icon={BookOpen} title={`${f.name} word for today`}>
                <p className="font-serif text-xl italic leading-snug" data-testid="demo-result-scripture">"{line.text}"</p>
                <p className="mt-1 text-sm text-[#6B5A52]">{line.ref}</p>
              </Row>
              {showZodiac && (
                <Row icon={Moon} title={`${z.name} · ${z.element}`}>
                  <p data-testid="demo-result-zodiac">{z.note}</p>
                </Row>
              )}
              <Row icon={Shirt} title={`Wear today · ${style.colorOfDay[0]} · ${look.vibe}${showZodiac ? "" : " · from your cycle"}`}>
                <p className="flex items-start gap-3" data-testid="demo-result-style">
                  <span className="mt-1 h-4 w-4 shrink-0 rounded-full" style={{ background: style.colorOfDay[1] }} />
                  <span>
                    {style.outfit}
                    <span className="mt-1.5 block text-sm text-[#6B5A52]" data-testid="demo-result-phase-style">{look.tip}</span>
                  </span>
                </p>
              </Row>
              <Row icon={Wallet} title="Today's spending">
                <p className="font-serif text-3xl leading-none text-[#2C221E]" data-testid="demo-result-spending">{formatMoney(spend?.perDay ?? 0, budget.currency)}</p>
                <p className="mt-1.5 text-sm text-[#6B5A52]">
                  of {formatMoney(spend?.amount ?? 0, budget.currency)} for your {p.name.toLowerCase()} phase.{" "}
                  {phase === "luteal" && <span className="font-semibold text-[#8C5A6B]">Spending often rises now, so pause before treats. </span>}
                  <button type="button" onClick={() => goTo("budget")} data-testid="demo-adjust-budget-link" className="font-semibold text-[#9E4429] underline underline-offset-2">Adjust budget</button>
                </p>
              </Row>
            </motion.div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
